import { saveAs } from 'file-saver'
//import { overallFueltypes } from '../components/plots/TimeSeriesCharts';

function resolve(path, obj, separator='.') {
    var properties = Array.isArray(path) ? path : path.split(separator)
    return properties.reduce((prev, curr) => prev?.[curr], obj)
}


// robust numeric coercion for raw values
export const getNshipFuelData = (d) => {
    let fdata = d.map(f => { return { ...f.nshipsfuel, name: `${f.year}-${f.month}`  } })
    return fdata
  }
/**
 * Normalize to % of FIRST non-zero total, and attach __raw fields.
 * - Does NOT mutate input rows.
 * - Computes raw values FIRST, then % from those raw values.
 */
export const normalizeToFirstTotalWithRaw = (rows, overallFueltypes) => {
  const fuelKeys = overallFueltypes.map(f => f.name);
  const toNum = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? 0 : Number(v));
  
  if (!Array.isArray(rows) || rows.length === 0) return [];

  // Find baseline total from the ORIGINAL raw values
  let baseTotal = 0;
  for (const row of rows) {
    const t = fuelKeys.reduce((acc, k) => acc + toNum(row[k]), 0);
    if (t > 0) { baseTotal = t; break; }
  }

  return rows.map((row) => {
    // 1) Capture the raw values from the ORIGINAL row
    const rawVals = Object.fromEntries(
      fuelKeys.map(k => [k, toNum(row[k])])
    );

    // 2) Compute the total for this row from rawVals
    const totalRaw = fuelKeys.reduce((acc, k) => acc + rawVals[k], 0);

    // 3) Compute % of start total (if baseTotal is 0, keep % at 0)
    const pctVals = (baseTotal > 0)
      ? Object.fromEntries(fuelKeys.map(k => [k, (rawVals[k] / baseTotal) * 100]))
      : Object.fromEntries(fuelKeys.map(k => [k, 0]));

    // 4) Build __raw fields from rawVals (not from row[k])
    const rawObj = Object.fromEntries(
      fuelKeys.map(k => [`${k}__raw`, rawVals[k]])
    );

    const totalVsStartPct = (baseTotal > 0) ? (totalRaw / baseTotal) * 100 : 0;

    // Return a NEW object; do not mutate `row`
    return {
      ...row,                    // keep original metadata like name
      ...pctVals,                // % fields used for plotting
      ...rawObj,                 // per-item raw numbers for tooltip
      __total_raw: totalRaw,     // raw total at this timestamp
      __total_vs_start_pct: totalVsStartPct, // total vs start in %
    };
  });
};

const aggregators = {
  skip: (acc, val) => (val),

  max: (acc,val) => acc == null ? val : Math.max(acc, val), 

  sum: (acc, val) => (acc ?? 0) + val,

  average: (acc, val) => (acc ?? 0) + val,

  objectSum: (acc, val) => {
    const out = { ...(acc ?? {}) };
    Object.entries(val ?? {}).forEach(([k, v]) => {
      out[k] = (out[k] ?? 0) + v;
    });
    return out;
  },
  objectAverage: (acc, val) => {
    const out = { ...(acc ?? {}) };
    Object.entries(val ?? {}).forEach(([k, v]) => {
      out[k] = (out[k] ?? 0) + v;
    });
    return out;
  }
};

const buildYearKey = row =>
  row.year != null && row.month != null
    ? `${row.year}-${String(row.month).padStart(2, "0")}`
    : row.name;

export function aggregateByYear(
  rows,
  {
    rules,
    dateKey = "name",
    outputYearKey = "year",
    defaultRule = "sum"
  }
) {
  const buckets = {};

  rows.forEach(srcRow => {
    const row = { ...srcRow };

    // build dateKey if missing
    if (!row[dateKey] && row.year != null && row.month != null) {
      row[dateKey] = buildYearKey(row);
    }

    const raw = row[dateKey];
    if (!raw) return;

    const year = raw.split("-")[0];

    if (!buckets[year]) {
      buckets[year] = {
        [outputYearKey]: Number(year),
        __count: 0
      };
    }

    Object.entries(row).forEach(([key, value]) => {
      const rule = rules[key] ?? defaultRule;

      if (rule === "drop") return;
      if (!(rule in aggregators)) return;

      buckets[year][key] =
        aggregators[rule](buckets[year][key], value);
    });

    buckets[year].__count += 1;
  });

  // finalize averages
  return Object.values(buckets).map(bucket => {
    const { __count, ...result } = bucket;

    Object.entries(rules).forEach(([key, rule]) => {
      if (rule === "average" && typeof result[key] === "number") {
        result[key] /= __count;
      }

      if (rule === "objectAverage" && result[key]) {
        Object.keys(result[key]).forEach(subKey => {
          result[key][subKey] /= __count;
        });
      }
    });

    return result;
  });
}

export function yearlyData(
  rows,
  {
    dateKey = "name",
    outputYearKey = "year",
    summedKeys = ["energy", "sum"]
  } = {}
) {
  const buckets = {};

  rows.forEach(originalRow => {
    const row = { ...originalRow };

    if (!row[dateKey] && row.year != null && row.month != null) {
      row[dateKey] = `${row.year}-${row.month}`;
    }

    const raw = row[dateKey];
    if (!raw) return;

    const year = raw.split("-")[0];

    if (!buckets[year]) {
      buckets[year] = {
        [outputYearKey]: Number(year),
        techshare: {},
        __count: 0
      };
    }

    Object.entries(row).forEach(([key, value]) => {
      if (key === dateKey || key === "month") return;

      if (typeof value === "number") {
        buckets[year][key] =
          (buckets[year][key] ?? 0) + value;
        return;
      }

      if (key === "techshare" && value && typeof value === "object") {
        Object.entries(value).forEach(([tKey, tVal]) => {
          buckets[year].techshare[tKey] =
            (buckets[year].techshare[tKey] ?? 0) + tVal;
        });
      }
    });

    buckets[year].__count += 1;
  });

  return Object.values(buckets).map(bucket => {
    const { __count, ...result } = bucket;

    Object.keys(result).forEach(key => {
      if (
        key === outputYearKey ||
        key === "techshare" ||
        summedKeys.includes(key)
      ) {
        return;
      }

      result[key] = result[key] / __count;
    });

    return result;
  });
}

export const downloadCSV = (filename, data, keys, headers) => {
  const blob = new Blob(
    ['\uFEFF' + headers.join() + '\r\n'].concat(
      data.map(el => keys.map(k => resolve(k, el)).join() + '\r\n')
    ),
    { type: "text/csv;charset=utf-16" }
  )

  saveAs(blob, filename)
}

export const parseFleetName = (fleet, portData) => {
return {fleet: fleet, 
    shiptype: fleet.split("_")[0], 
    portA: findPortName(fleet.split("_")[1], portData),
    portB: findPortName(fleet.split("_")[2], portData),
}
}

export const findPortName = (locode, portData) => {
    const ports = portData.filter(el => el.locode === locode)
    if (ports.length) {
        return ports[0].name
    } else {
       return locode
    }
   }